import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, CATEGORY_LABELS, GENDER_LABELS } from "../lib/api";
import StartupCard from "../components/StartupCard";
import { Search, X, SlidersHorizontal } from "lucide-react";

const CATEGORIES = ["trendy", "traditional", "artisanal", "formal", "athleisure", "eco_friendly", "occasion_wear"];
const GENDERS = ["men", "women", "unisex"];
const SORTS = [
    { id: "featured", label: "Editor's picks" },
    { id: "newest", label: "Newest" },
    { id: "name", label: "A → Z" },
    { id: "followers", label: "Most followed" },
];

export default function Discover() {
    const [params, setParams] = useSearchParams();
    const [gender, setGender] = useState(params.get("gender") || "");
    const [category, setCategory] = useState(params.get("category") || "");
    const [region, setRegion] = useState(params.get("region") || "");
    const [sort, setSort] = useState(params.get("sort") || "featured");
    const [q, setQ] = useState(params.get("q") || "");
    const [startups, setStartups] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        api.get("/regions").then((r) => setRegions(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        const query = {};
        if (gender) query.gender = gender;
        if (category) query.category = category;
        if (region) query.region = region;
        if (sort) query.sort = sort;
        if (q) query.q = q;
        api.get("/startups", { params: query })
            .then((r) => setStartups(r.data))
            .finally(() => setLoading(false));
        const p = {};
        Object.entries(query).forEach(([k, v]) => { if (v) p[k] = v; });
        setParams(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gender, category, region, sort, q]);

    const activeCount = useMemo(() =>
        [gender, category, region, q].filter(Boolean).length + (sort !== "featured" ? 1 : 0),
    [gender, category, region, q, sort]);

    const clearAll = () => {
        setGender(""); setCategory(""); setRegion(""); setSort("featured"); setQ("");
    };

    const regionName = (id) => regions.find((r) => r.id === id)?.name || id;
    const sortLabel = (id) => SORTS.find((s) => s.id === id)?.label || id;

    return (
        <main className="min-h-screen">
            {/* Sticky compact filter bar */}
            <div className="sticky top-16 z-30 bg-black/90 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 md:py-4">
                    <div className="flex flex-col md:flex-row gap-2 md:gap-3 items-stretch md:items-center">
                        {/* Search */}
                        <div className="relative flex-1 min-w-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                            <input
                                data-testid="discover-search-input"
                                value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="Search brands, styles, cities..."
                                className="w-full h-11 pl-10 pr-4 rounded-md bg-brand-ink border border-white/15 focus:border-brand-saffron focus:outline-none text-white placeholder:text-white/40 transition-colors"
                            />
                        </div>
                        {/* Sort + filter buttons row */}
                        <div className="flex gap-2">
                            <select
                                data-testid="discover-sort-select"
                                value={sort} onChange={(e) => setSort(e.target.value)}
                                className="flex-1 md:flex-none h-11 px-3 md:px-4 rounded-md bg-brand-ink border border-white/15 text-white focus:border-brand-saffron focus:outline-none font-mono-tag text-xs uppercase"
                            >
                                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                            <button
                                data-testid="discover-toggle-filters"
                                onClick={() => setShowAdvanced((v) => !v)}
                                className={`h-11 px-4 rounded-md border flex items-center gap-2 font-mono-tag text-xs uppercase transition-colors shrink-0 ${
                                    showAdvanced || activeCount > 0 ? "bg-brand-saffron text-black border-brand-saffron" : "bg-brand-ink border-white/15 text-white hover:border-brand-saffron"
                                }`}
                            >
                                <SlidersHorizontal className="w-3.5 h-3.5" />
                                <span className="hidden xs:inline">Filters</span>
                                {activeCount > 0 && (
                                    <span data-testid="active-filters-count" className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                                        showAdvanced || activeCount > 0 ? "bg-black/20" : "bg-brand-saffron text-black"
                                    }`}>{activeCount}</span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Applied filter chips */}
                    {activeCount > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {q && <FilterPill label={`"${q}"`} testid="chip-q" onRemove={() => setQ("")} />}
                            {gender && <FilterPill label={GENDER_LABELS[gender]} testid={`chip-gender-${gender}`} onRemove={() => setGender("")} />}
                            {category && <FilterPill label={CATEGORY_LABELS[category]} testid={`chip-category-${category}`} onRemove={() => setCategory("")} />}
                            {region && <FilterPill label={regionName(region)} testid={`chip-region-${region}`} onRemove={() => setRegion("")} />}
                            {sort !== "featured" && <FilterPill label={sortLabel(sort)} testid={`chip-sort-${sort}`} onRemove={() => setSort("featured")} />}
                            <button
                                data-testid="clear-all-filters"
                                onClick={clearAll}
                                className="font-mono-tag text-[11px] uppercase text-brand-magenta hover:text-white transition-colors ml-1"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* Advanced filter panel */}
                {showAdvanced && (
                    <div className="border-t border-white/10 bg-brand-inkWarm/70 backdrop-blur max-h-[60vh] overflow-y-auto">
                        <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-5 md:py-6 space-y-5">
                            <FilterGroup label="Gender" testidPrefix="gender-filter">
                                <ChipRow items={[{ id: "", label: "All" }, ...GENDERS.map((g) => ({ id: g, label: GENDER_LABELS[g] }))]}
                                    active={gender} onChange={setGender} accent="saffron" testidPrefix="gender-filter" />
                            </FilterGroup>
                            <FilterGroup label="Category" testidPrefix="category-filter">
                                <ChipRow items={[{ id: "", label: "All" }, ...CATEGORIES.map((c) => ({ id: c, label: CATEGORY_LABELS[c] }))]}
                                    active={category} onChange={setCategory} accent="saffron" testidPrefix="category-filter" />
                            </FilterGroup>
                            <FilterGroup label="Region" testidPrefix="region-filter">
                                <ChipRow items={[{ id: "", label: "All India" }, ...regions.map((r) => ({ id: r.id, label: r.name }))]}
                                    active={region} onChange={setRegion} accent="marigold" testidPrefix="region-filter" />
                            </FilterGroup>
                        </div>
                    </div>
                )}
            </div>

            {/* Header + results */}
            <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-8 md:py-14">
                <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ discover</p>
                <h1 data-testid="discover-title" className="font-display font-extrabold text-3xl sm:text-4xl md:text-6xl tracking-tighter uppercase mb-3">
                    All the <span className="text-brand-saffron">underdogs.</span>
                </h1>
                <p data-testid="startup-count" className="font-mono-tag text-xs uppercase text-white/50 mb-8">
                    {loading ? "loading..." : `${startups.length} brand${startups.length === 1 ? "" : "s"} found`}
                </p>

                {startups.length === 0 && !loading ? (
                    <div className="border border-dashed border-white/15 rounded-md py-24 text-center">
                        <p className="font-display text-2xl mb-2">No brands match this vibe (yet).</p>
                        <p className="text-white/60 text-sm mb-6">Try a different filter — new drops arriving weekly.</p>
                        {activeCount > 0 && (
                            <button onClick={clearAll} className="font-mono-tag text-xs uppercase text-brand-saffron border border-brand-saffron px-4 py-2 rounded-md hover:bg-brand-saffron hover:text-black transition-colors">
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[260px]">
                        {startups.map((s, i) => (
                            <StartupCard key={s.id} startup={s} variant={i % 5 === 0 ? "tall" : "default"} />
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}

function FilterPill({ label, onRemove, testid }) {
    return (
        <span data-testid={testid} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-saffron/15 border border-brand-saffron/40 text-brand-saffron font-mono-tag text-[11px] uppercase">
            {label}
            <button onClick={onRemove} className="hover:text-white" aria-label={`Remove ${label}`}>
                <X className="w-3 h-3" />
            </button>
        </span>
    );
}

function FilterGroup({ label, children }) {
    return (
        <div>
            <p className="font-mono-tag text-[10px] uppercase text-white/50 mb-2">/ {label}</p>
            {children}
        </div>
    );
}

function ChipRow({ items, active, onChange, accent, testidPrefix }) {
    const activeStyles = accent === "marigold"
        ? "bg-brand-marigold text-black border-brand-marigold"
        : "bg-brand-saffron text-black border-brand-saffron";
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((it) => {
                const isActive = active === it.id;
                return (
                    <button
                        key={it.id || "all"}
                        data-testid={`${testidPrefix}-${it.id || "all"}`}
                        onClick={() => onChange(it.id)}
                        className={`px-4 py-1.5 rounded-full font-mono-tag text-[11px] uppercase border transition-colors ${
                            isActive ? activeStyles : "bg-transparent text-white border-white/20 hover:border-white/50"
                        }`}
                    >
                        {it.label}
                    </button>
                );
            })}
        </div>
    );
}
